import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Building2, Upload, Save, Clock } from 'lucide-react'

interface Organization {
  id: string;
  name: string;
  company_name?: string;
  org_number?: string;
}

interface GeneralTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function GeneralTab({ organization, onSave }: GeneralTabProps) {
  const [formData, setFormData] = useState({
    company_name: organization.company_name || '',
    org_number: organization.org_number || '',
    logo_url: '',
    timezone: 'Europe/Stockholm'
  })
  const [dirty, setDirty] = useState(false)
  const draftKey = `org-settings:general:${organization?.id ?? 'default'}`

  // Ladda ev. utkast från localStorage när organisationen byts
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey)
      if (raw) {
        const draft = JSON.parse(raw)
        if (draft && typeof draft === 'object') {
          setFormData((prev) => ({ ...prev, ...draft }))
        }
      }
    } catch (e) {
      console.warn('Kunde inte läsa organisationsutkast:', e)
    }
  }, [draftKey])

  // Håll formulär i synk om props ändras (men skriv inte över lokala ändringar)
  useEffect(() => {
    if (dirty) return
    setFormData(prev => ({
      ...prev,
      company_name: organization.company_name || '',
      org_number: organization.org_number || ''
    }))
  }, [organization.company_name, organization.org_number, dirty])

  // Skriv utkast till localStorage vid ändringar
  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(formData))
    } catch (e) {
      console.warn('Kunde inte spara organisationsutkast:', e)
    }
  }, [formData, draftKey])

  const [versionHistory] = useState([
    // Real version history would be fetched from audit_log table
  ])

  const handleSave = () => {
    onSave(formData)
    try { localStorage.removeItem(draftKey) } catch {}
    setDirty(false)
  }

  const timezones = [
    { value: 'Europe/Stockholm', label: 'Europa/Stockholm (CET/CEST)' },
    { value: 'Europe/London', label: 'Europa/London (GMT/BST)' },
    { value: 'America/New_York', label: 'Amerika/New York (EST/EDT)' },
    { value: 'America/Los_Angeles', label: 'Amerika/Los Angeles (PST/PDT)' }
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organisationsuppgifter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Företagsnamn</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => {
                  setDirty(true)
                  setFormData(prev => ({ ...prev, company_name: e.target.value }))
                }}
                placeholder="Ange företagsnamn"
              />
            </div>
            <div>
              <Label htmlFor="org_number">Organisationsnummer</Label>
              <Input
                id="org_number"
                value={formData.org_number}
                onChange={(e) => {
                  setDirty(true)
                  setFormData(prev => ({ ...prev, org_number: e.target.value }))
                }}
                placeholder="XXXXXX-XXXX"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="timezone">Tidszon</Label>
            <Select value={formData.timezone} onValueChange={(value) => { setDirty(true); setFormData(prev => ({ ...prev, timezone: value })) }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Logotyp</Label>
            <div className="flex items-center gap-4 mt-2">
              <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <Building2 className="h-8 w-8 text-gray-400" />
              </div>
              <Button variant="outline" className="gap-2">
                <Upload className="h-4 w-4" />
                Ladda upp logotyp
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Rekommenderad storlek: 200x200px, max 2MB (PNG, JPG)
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} className="gap-2">
              <Save className="h-4 w-4" />
              Spara ändringar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Ändringshistorik
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {versionHistory.map((version, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                <div>
                  <p className="text-sm font-medium">{version.changes}</p>
                  <p className="text-xs text-muted-foreground">av {version.user}</p>
                </div>
                <p className="text-xs text-muted-foreground">{version.date}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}